import SelectInput from 'components/form/SelectInput';
import { Formik } from 'formik';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CountriesService from 'services/CountriesService';
import { AccountModel, CompanyModel, CountryModel, MembershipModel } from 'services/EpdClient';
import AccountsService from 'services/admin/AccountsService';
import styled from 'styled-components';
import { ButtonSmall, ErrorText, FormMode, StyledForm } from 'styles/Styles.styled';
import * as Yup from 'yup';

import { useTheme } from 'styled-components';
import { Theme } from 'styles/theme';
import {
  ALLOWED_SYMBOLS_ERROR,
  allowedGeneralCharsRegex,
  allowedSymbolsLink,
  createAllowedSymbolsBottomErrorsTextInputProps,
} from 'util/allowedSymbolsValidationUtils';
import { ProcessStatus } from '../../constants/constants';
import { ExpandButton } from '../buttons/ExpandButton';
import TextInput from '../form/TextInput';
import { TableRow } from '../member/ManageMembers';

function useAccountRowHook(account: AccountModel, company: CompanyModel, onCompleted: () => Promise<void>) {
  const [isEditMode, setEditMode] = useState(false);
  const [status, setStatus] = useState<ProcessStatus>(ProcessStatus.None);
  const [countries, setCountries] = useState<CountryModel[]>([]);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    async function fetch() {
      const resultCountries = await CountriesService.getCountries();
      setCountries(resultCountries);
    }
    fetch();
  }, []);

  const deleteAccount = async () => {
    try {
      setStatus(ProcessStatus.Fetching);
      setDeleteErrorMessage(null);
      await AccountsService.deleteAccount(account.id || '');
      await onCompleted();
      setStatus(ProcessStatus.Success);
    } catch (error: any) {
      setEditMode(false);
      setStatus(ProcessStatus.Error);
      try {
        const parsed = JSON.parse(error?.response);
        setDeleteErrorMessage(parsed?.message ?? null);
      } catch {
        setDeleteErrorMessage(null);
      }
    }
  };

  const saveChanges = async (update: AccountModel) => {
    try {
      setStatus(ProcessStatus.Fetching);
      await AccountsService.updateAccount(update);
      await onCompleted();
      setEditMode(false);
      setStatus(ProcessStatus.None);
    } catch (error) {
      setEditMode(false);
      setStatus(ProcessStatus.Error);
    }
  };

  return { status, deleteAccount, saveChanges, isEditMode, setEditMode, countries, deleteErrorMessage };
}

const AccountRow: FunctionComponent<{
  account: AccountModel;
  company: CompanyModel;
  accountManagers: MembershipModel[];
  onCompleted: () => Promise<void>;
  isAdmin: boolean;
}> = ({ account, company, accountManagers, onCompleted, isAdmin }) => {
  const { status, isEditMode, setEditMode, deleteAccount, saveChanges, deleteErrorMessage } = useAccountRowHook(
    account,
    company,
    onCompleted
  );
  const theme = useTheme() as Theme;

  const { t } = useTranslation();

  if (status === ProcessStatus.Success) {
    return null;
  }

  const REQUIRED_MSG = t('messages.required');
  const bottomErrorsTextInputProps = createAllowedSymbolsBottomErrorsTextInputProps({
    theme,
    requiredMsg: REQUIRED_MSG,
    allowedSymbolsLink,
  });

  const accountSchema = Yup.object<AccountModel>({
    name: Yup.string().required(t('messages.required')).matches(allowedGeneralCharsRegex, ALLOWED_SYMBOLS_ERROR),
    /* The model has the email defined as `string | undefined`, and that is not
    the same as being nullable. The model received from the server _does_ have
    the value set to null. I believe the model generated from nswag is
    incorrect. Long story short: we have to cast to `any` for this to compile */
  });

  return (
    <Row style={{ alignItems: 'flex-start' }}>
      {isEditMode ? (
        <Formik initialValues={account} validationSchema={accountSchema} onSubmit={(values) => saveChanges(values)}>
          {({ isSubmitting, values, dirty, isValid }) => (
            <StyledForm mode={FormMode.Inline} style={{ flex: 1 }}>
              <FormColumn>
                <TextInput label={t('name')} name="name" supressErrors={false} {...bottomErrorsTextInputProps} />
              </FormColumn>
              <FormColumn>
                <SelectInput
                  label={t('manageAccounts.editAccount.manager')}
                  name="accountManagerUserId"
                  style={{ margin: '0.5rem 0 1rem 0' }}
                  supressErrors={true}
                >
                  {accountManagers?.map((option) => (
                    <option value={option.userId} key={option.userId}>
                      {option.userName}
                    </option>
                  ))}
                </SelectInput>
              </FormColumn>
              <FormColumn>
                <ButtonContainer>
                  <ButtonSmall disabled={isSubmitting || !isValid || !dirty}>
                    {isSubmitting ? t('messages.saving') : t('messages.saveSettings')}
                  </ButtonSmall>
                  {isAdmin && (
                    <ButtonSmall
                      style={{ marginLeft: '1rem' }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.confirm(t('manageAccounts.messages.confirmDeletion')) && deleteAccount();
                      }}
                    >
                      {t('delete')}
                    </ButtonSmall>
                  )}
                </ButtonContainer>
              </FormColumn>
            </StyledForm>
          )}
        </Formik>
      ) : (
        <>
          <div style={{ flexGrow: 1 }}>
            {account.name}
            {status === ProcessStatus.Error && (
              <ErrorText dangerouslySetInnerHTML={{ __html: deleteErrorMessage ?? 'Error!' }} />
            )}
          </div>
          <div>{account.accountManagerName}</div>
        </>
      )}
      <ExpandButton isOpen={isEditMode} onClick={() => setEditMode(!isEditMode)} />
    </Row>
  );
};

export default AccountRow;

const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 48%;
  box-sizing: border-box;
  padding-right: 2rem;
`;

const Row = styled(TableRow)`
  button:last-child {
    margin-left: 1rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex: 1;
  padding-right: 2rem;
`;
